// src/pages/Dashboard.jsx
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  apiMasterXmlStatus, apiUploadMasterXml,
  apiProcess, apiJobStatus,
  apiReviewBills, apiReviewBank,
  apiConfirmBills, apiConfirmBank,
  apiExportBills, apiExportBank,
  downloadCalibrationKit, apiGetCredits,
  apiJoinWaitlist, apiCheckBankPassword,
  apiDeleteMasterXml,apiCancelJob,
  apiSearchLedgers,
} from '../services/api';
import styles from './Dashboard.module.css';
import FeedbackWidget from '../components/FeedbackWidget';
import { recalcLineItem, recalcHeaderFromItems } from '../utils/taxCalculator';

const BILL_TYPES    = '.pdf,.jpg,.jpeg,.png,.webp,.xml,.docx,.xlsx,.xls';
const BANK_TYPES    = '.pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xls,.csv';
const PLUS_BUNDLE_LIVE = true;  // flip to true when you're ready to launch

// ── Processing Stepper: stage config ─────────────────────────────────────────
const STAGE_ORDER = [
  'queued', 'preprocessing', 'discovering',
  'extracting', 'bank_processing', 'mapping', 'ready'
];

const STEPS = [
  {
    id: 'discovery',
    title: 'Pre-processing',
    backendStages: ['queued', 'preprocessing', 'discovering'],
    detailFallback: 'Preparing files...',
  },
  {
    id: 'extraction',
    title: 'Extracting Bills',
    backendStages: ['extracting'],
    detailFallback: 'Extracting your bills...',
    showBillCounter: true,
  },
  {
    id: 'bank',
    title: 'Bank Statement',
    backendStages: ['bank_processing'],
    detailFallback: 'Processing bank statement...',
    optional: true,
  },
  {
    id: 'postprocessing',
    title: 'Post-Processing',
    backendStages: ['mapping'],
    detailFallback: 'Mapping ledgers and matching bank transactions...',
  },
  {
    id: 'ready',
    title: 'Ready for Review',
    backendStages: ['ready', 'review'],
    detailFallback: 'Review your bills and bank matches on the dashboard.',
  },
];

function getStepStatuses(currentStage, hasBankFile) {
  const steps = hasBankFile ? STEPS : STEPS.filter(s => s.id !== 'bank');
  const effectiveStage = currentStage === 'review' ? 'ready' : currentStage;
  const currentIdx = STAGE_ORDER.indexOf(effectiveStage);

  return steps.map(step => {
    const stepIdxs = step.backendStages.map(s => STAGE_ORDER.indexOf(s));
    const isActive = step.backendStages.includes(effectiveStage);
    const isDone   = currentIdx > Math.max(...stepIdxs);
    return { ...step, status: isActive ? 'active' : isDone ? 'done' : 'pending' };
  });
}

// ── Fixed-ring "chase" spinner — ring never rotates, color chases through
// fixed curved arc segments that sit ON the ring (tangential), not spokes
// pointing at the center. ────────────────────────────────────────────────
const SPINNER_SEGMENTS = 8;
function StepSpinner() {
  const r = 16;
  const circumference = 2 * Math.PI * r;
  const slot = circumference / SPINNER_SEGMENTS;
  const dashLen = slot * 0.55;
  const gapLen = circumference - dashLen;

  const segs = [];
  for (let i = 0; i < SPINNER_SEGMENTS; i++) {
    const offset = -(i * slot);
    const delay = (i * (1.1 / SPINNER_SEGMENTS)).toFixed(3);
    segs.push(
      <circle
        key={i}
        cx="20" cy="20" r={r}
        fill="none"
        strokeDasharray={`${dashLen} ${gapLen}`}
        strokeDashoffset={offset}
        className={styles.spinnerTick}
        style={{ animationDelay: `${delay}s` }}
      />
    );
  }
  return (
    <svg className={styles.stepSpinner} viewBox="0 0 40 40" fill="none">
      {segs}
    </svg>
  );
}

function StepCheck() {
  return (
    <svg className={styles.stepCheck} viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="8" fill="currentColor" />
      <path d="M4.5 8.2l2.3 2.3L11.5 5.6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function StepDot() {
  return <span className={styles.stepDot} />;
}

// ── Text builders for "done" states — separate from the live/active text ───
function pluralize(n, word) { return `${n} ${word}${n === 1 ? '' : 's'}`; }

function buildFoundText(discoveredBills, totalFiles, hasBank, hasMaster) {
  const parts = [pluralize(discoveredBills, 'bill')];
  if (hasBank)   parts.push('1 bank statement');
  if (hasMaster) parts.push('1 Master.xml');

  let joined;
  if (parts.length === 1) joined = parts[0];
  else if (parts.length === 2) joined = `${parts[0]} & ${parts[1]}`;
  else joined = `${parts[0]}, ${parts[1]}, & ${parts[2]}`;

  return `Found ${joined} from ${pluralize(totalFiles, 'file')}.`;
}

// ── Processing Stepper ────────────────────────────────────────────────────────
function ProcessingStepper({ jobStatus, filesDone, filesTotal, hasBankFile, intakeText, discoveredBills, totalFilesSubmitted, masterUploaded }) {
  const currentStage = jobStatus?.status === 'review' ? 'review' : (jobStatus?.current_stage || 'queued');
  const detail       = jobStatus?.stage_detail || '';
  const steps        = getStepStatuses(currentStage, hasBankFile);

  return (
    <div className={styles.stepperWrap}>
      <div className={styles.stepper}>
        {steps.map((step, i) => {
          const isLast = i === steps.length - 1;

          // Live (active) text: prefer real backend telemetry, fall back to
          // dynamic intake text on discovery, else the generic fallback.
          // Done text: explicit per-stage message, NEVER inherits the last
          // active-state string.
          let liveDetail = step.detailFallback;

          if (step.status === 'active') {
            if (detail && detail !== 'Preparing files...' && !detail.includes('Detecting file types')) {
              liveDetail = detail;
            } else if (step.id === 'discovery') {
              liveDetail = intakeText;
            }
          } else if (step.status === 'done') {
            if (step.id === 'discovery') {
              liveDetail = buildFoundText(discoveredBills, totalFilesSubmitted, hasBankFile, masterUploaded);
            } else if (step.id === 'extraction') {
              liveDetail = `Extracted ${pluralize(filesTotal, 'bill')} successfully.`;
            } else if (step.id === 'bank') {
              liveDetail = 'Extracted bank statement successfully.';
            } else if (step.id === 'postprocessing') {
              liveDetail = 'Mapping and cross-matching complete.';
            }
          }

          return (
            <div key={step.id} className={`${styles.stepRow} ${styles[`stepRow_${step.status}`]}`}>
              <div className={styles.stepRail}>
                <div className={`${styles.stepIcon} ${styles[`stepIcon_${step.status}`]}`}>
                  {step.status === 'done' && <StepCheck />}
                  {step.status === 'active' && <StepSpinner />}
                  {step.status === 'pending' && <StepDot />}
                </div>
                {!isLast && (
                  <div className={`${styles.stepLine} ${step.status === 'done' ? styles.stepLineDone : ''}`} />
                )}
              </div>

              <div className={styles.stepBody}>
                <span className={`${styles.stepTitle} ${styles[`stepTitle_${step.status}`]}`}>
                  {step.title}
                </span>

                {step.status !== 'pending' && (
                  <div style={{ overflow: 'hidden' }}>
                    {/* key={liveDetail} forces a remount so the text-flip animation
                        replays every time the message actually changes — this is
                        what makes the "live text flips to reveal next text" effect
                        work while a stage is active, and also plays once when a
                        stage's message swaps to its done-state text. */}
                    <span key={liveDetail} className={styles.stepDetail}>
                      {liveDetail}
                    </span>
                  </div>
                )}

                {step.showBillCounter && step.status === 'active' && filesTotal > 0 && (
                  <div className={styles.stepSubProgress}>
                    <div className={styles.stepSubHeader}>
                      <span className={styles.stepSubCount}>{filesDone} / {filesTotal} bills</span>
                      <span className={styles.stepSubPct}>{Math.round((filesDone / filesTotal) * 100)}%</span>
                    </div>
                    <div className={styles.stepSubTrack}>
                      <div
                        className={styles.stepSubBar}
                        style={{ width: `${Math.round((filesDone / filesTotal) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className={styles.processingNote}>
        Processing on our AI servers. High traffic may cause slight delays — please keep this page open and do NOT refresh.
      </p>
    </div>
  );
}

// ── File Upload Zone (Bills / Bank) ──────────────────────────────────────────
function UploadZone({ label, hint, formats, accept, files, onFiles, disabled, single }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [sizeError, setSizeError] = useState('');

  const addFiles = (incoming) => {
    if (disabled) return;
    const arr = Array.from(incoming);
    if (!arr.length) return;

    const MAX_SIZE = 6 * 1024 * 1024;
    const oversized = arr.filter(f => f.size > MAX_SIZE);
    const valid = arr.filter(f => f.size <= MAX_SIZE);

    if (oversized.length > 0) {
      setSizeError(
        `${oversized.map(f => f.name).join(', ')} ${oversized.length > 1 ? 'exceed' : 'exceeds'} the 6MB limit.`
      );
    } else {
      setSizeError('');
    }

    if (!valid.length) return;
    if (single) { onFiles([valid[0]]); return; }
    onFiles(prev => {
      const names = new Set(prev.map(f => f.name));
      return [...prev, ...valid.filter(f => !names.has(f.name))];
    });
  };

  const removeFile = (name) => onFiles(prev => prev.filter(f => f.name !== name));

  const fmt = (b) => b < 1048576 ? `${(b/1024).toFixed(1)} KB` : `${(b/1048576).toFixed(1)} MB`;
  const ext = (n) => n.split('.').pop().toUpperCase();

  return (
    <div className={styles.uploadCard}>
      <div
        className={`${styles.dropZone} ${dragging ? styles.dragging : ''} ${disabled ? styles.disabled : ''}`}
        onClick={() => !disabled && inputRef.current.click()}
        onDragOver={e => { e.preventDefault(); if (!disabled) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
        role="button" tabIndex={disabled ? -1 : 0}
        onKeyDown={e => e.key === 'Enter' && !disabled && inputRef.current.click()}
      >
        <input ref={inputRef} type="file" multiple={!single} accept={accept}
          style={{ display: 'none' }} onChange={e => addFiles(e.target.files)} />
        <div className={styles.plusIcon}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <line x1="12" y1="4" x2="12" y2="20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <line x1="4" y1="12" x2="20" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div>
        <span className={styles.dropLabel}>{label}</span>
        <span className={styles.dropHint}>{dragging ? 'Drop here' : hint}</span>
        <span className={styles.dropFormats}>{formats}</span>
      </div>
      {sizeError && <div className={styles.sizeError}>{sizeError}</div>}
      {files.length > 0 && (
        <div className={styles.fileList}>
          {files.map(f => (
            <div key={f.name} className={styles.fileItem}>
              <span className={styles.fileExt}>{ext(f.name)}</span>
              <div className={styles.fileInfo}>
                <span className={styles.fileName} title={f.name}>{f.name}</span>
                <span className={styles.fileSize}>{fmt(f.size)}</span>
              </div>
              {!disabled && (
                <button className={styles.removeBtn} onClick={() => removeFile(f.name)} type="button">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <line x1="1" y1="1" x2="11" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    <line x1="11" y1="1" x2="1" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Bank Match Selector ──────────────────────────────────────────────────────
function BankMatchSelector({ txn, bills, selected, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const btnRef  = useRef(null);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onScroll = () => {
      if (btnRef.current) {
        const rect = btnRef.current.getBoundingClientRect();
        setDropPos({
          top:  rect.bottom + 4,
          left: Math.min(rect.left, window.innerWidth - 348)
        });
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropPos({
        top:  rect.bottom + 4,
        left: Math.min(rect.left, window.innerWidth - 348)
      });
    }
    setOpen(o => !o);
  };
  const txnAmount = parseFloat(txn.debit || txn.credit || 0);
  const selectedSum = selected.reduce((s, l) => s + parseFloat(l.amount || 0), 0);
  const checksumOk = selected.length === 0 || Math.abs(selectedSum - txnAmount) < 0.02;

  const isSelected = (resultId) => selected.some(l => l.result_id === resultId);

  const toggle = (bill) => {
    if (isSelected(bill.result_id)) {
      onChange(selected.filter(l => l.result_id !== bill.result_id));
    } else {
      const allocated = selectedSum;
      const remaining = Math.max(0, txnAmount - allocated);
      const billTotal = parseFloat(bill.total_amount || bill.taxable_amount || 0);
      const defaultAmt = remaining > 0
        ? Math.min(remaining, billTotal)
        : billTotal;
      onChange([...selected, {
        result_id:      bill.result_id,
        invoice_number: bill.invoice_number,
        party_name:     bill.party_name,
        amount:         parseFloat(defaultAmt.toFixed(2))
      }]);
    }
  };

  const updateAmount = (resultId, val) => {
    onChange(selected.map(l =>
      l.result_id === resultId ? { ...l, amount: parseFloat(val) || 0 } : l
    ));
  };

  return (
    <div className={styles.bankMatchWrap} ref={wrapRef}>
      <button
        type="button"
        ref={btnRef}
        disabled={disabled}
        className={`${styles.bankMatchBtn} ${selected.length > 0 ? styles.bankMatchBtnActive : ''} ${!checksumOk ? styles.bankMatchBtnWarn : ''}`}
        onClick={handleToggle}
      >
        {selected.length === 0
          ? 'No Match'
          : `${selected.length} invoice${selected.length > 1 ? 's' : ''} — ₹${selectedSum.toFixed(2)}`
        }
        {!checksumOk && selected.length > 0 && (
          <span className={styles.checksumWarn}> ≠ ₹{txnAmount.toFixed(2)}</span>
        )}
        <span className={styles.bankMatchArrow}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className={styles.bankMatchDropdown} style={{ top: dropPos.top, left: dropPos.left }}>
          <div className={styles.bankMatchHeader}>
            Txn: ₹{txnAmount.toFixed(2)} | Selected: ₹{selectedSum.toFixed(2)}
            {checksumOk && selected.length > 0 && <span className={styles.checksumOk}> ✓</span>}
            {!checksumOk && selected.length > 0 && <span className={styles.checksumFail}> ✗ mismatch</span>}
          </div>
          {bills.map(bill => (
            <label key={bill.result_id} className={styles.bankMatchOption}>
              <input
                type="checkbox"
                checked={isSelected(bill.result_id)}
                onChange={() => toggle(bill)}
              />
              <span className={styles.bankMatchLabel}>
                {bill.party_name} — {bill.invoice_number}
                <span className={styles.bankMatchAmt}>₹{Number(bill.total_amount || bill.taxable_amount || 0).toLocaleString('en-IN')}</span>
              </span>
              {isSelected(bill.result_id) && (
                <input
                  type="number"
                  className={styles.bankMatchAmtInput}
                  value={selected.find(l => l.result_id === bill.result_id)?.amount || ''}
                  onChange={e => updateAmount(bill.result_id, e.target.value)}
                  onClick={e => e.stopPropagation()}
                  placeholder="Split amt"
                  step="0.01"
                />
              )}
            </label>
          ))}
          <button
            type="button"
            className={styles.bankMatchClear}
            onClick={() => { onChange([]); setOpen(false); }}
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
}

// ── Ledger Type-to-Search ─────────────────────────────────────────────────
function LedgerSearchInput({ value, onChange, ledgers, disabled }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  const query = (value || '').toLowerCase();
  const matches = query
    ? ledgers.filter(l => l.toLowerCase().includes(query)).slice(0, 20)
    : ledgers.slice(0, 20);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        className={styles.cellInput}
        value={value}
        disabled={disabled}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
      />
      {open && matches.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 50,
          background: '#fff', border: '1px solid #ccc', borderRadius: 4,
          maxHeight: 180, overflowY: 'auto', minWidth: 200,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          {matches.map((l) => (
            <div
              key={l}
              style={{ padding: '6px 10px', cursor: 'pointer', fontSize: '0.85rem' }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => { e.stopPropagation(); onChange(l); setOpen(false); }}
            >
              {l}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Field Confidence: parsing, reason text, wrapper ──────────────────────────
function parseFieldConfidence(raw) {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return {}; }
  }
  return raw;
}

function round2(n) { return Math.round((parseFloat(n) || 0) * 100) / 100; }

function fieldReasonSentence(fc) {
  if (!fc) return '';
  const calc = fc.calculated_value != null ? `₹${Number(fc.calculated_value).toLocaleString('en-IN')}` : null;
  switch (fc.reason) {
    case 'math_mismatch':
      return calc ? `We calculate ${calc}, but the bill shows a different total.` : `The bill's totals don't add up as printed.`;
    case 'line_math_mismatch':
      return calc ? `We calculate ${calc} for this line, but the bill shows a different amount.` : `This line's rate × qty doesn't match its amount.`;
    case 'invalid_format':
      return 'This value has an unexpected format, likely misread.';
    case 'checksum_failed':
      return 'This GSTIN fails the standard checksum check, likely misread.';
    case 'unparseable':
      return "We couldn't read this as a valid date.";
    case 'future_date':
      return 'This date is in the future, likely misread.';
    case 'implausibly_old':
      return 'This date is over three years old, worth double checking.';
    case 'plausible_unverifiable':
      return "This date looks valid, but we can't independently confirm the exact digits.";
    case 'missing':
      return 'This field was not found on the bill.';
    case 'new_uncorroborated':
      return "This party isn't in your Master.xml yet, so there is nothing to cross check it against.";
    case 'unverifiable_present':
      return "We can't independently verify this, please check it against the bill.";
    case 'inconsistent_spelling_same_gstin':
      return 'The same GSTIN appears with a different spelling elsewhere in this batch.';
    case 'duplicate_in_batch':
      return 'This invoice number repeats for the same vendor in this batch, a possible duplicate.';
    case 'gst_sum_mismatch':
      return calc ? `Line items' tax adds up to ${calc}, but the bill's own GST total disagrees. Check the line items below.` : `The bill's GST total does not match the sum of line item tax.`;
    case 'line_items_sum_mismatch':
      return calc ? `Line items add up to ${calc}. Check the item rates below, as one of them may be misread.` : `Line items do not add up to the bill's stated taxable amount. Check the item rates below.`;
    case 'description_ambiguous':
      return 'This item description looked unclear or handwriting heavy, please check it against the bill.';
    case 'category_mix_suspected':
      return 'This description may combine two different products, please check it against the bill.';
    case 'unsafe_prefix_character':
      return 'This value contains characters that could be unsafe in a spreadsheet, please review it.';
    default:
      return 'Please double check this value against the bill.';
  }
}

function FieldConfWrap({ fc, onApply, children }) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef(null);

  // Click-outside listener to dissolve the popover automatically
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(event) {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // Inject the hover pop-out class directly into the wrapped input
  const modifiedChild = children;

  if (!fc || !fc.toggle) return modifiedChild;

  const cls = fc.toggle === 'mismatch' ? styles.fieldMismatch : styles.fieldConfirmation;
  return (
    <span className={styles.fieldConfWrap}>
      <span className={cls}>{modifiedChild}</span>
      <button
        type="button"
        className={styles.fieldFlagBtn}
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
      >!</button>
      {open && (
        <div className={styles.fieldFlagPopover} ref={popoverRef} onClick={e => e.stopPropagation()}>
          <p style={{ margin: '0 0 6px 0' }}>{fieldReasonSentence(fc)}</p>
          
          {/* Render 'Apply Fix' if backend provided a calculated mathematical correction */}
          {fc.calculated_value != null && (
            <div style={{ marginTop: '8px', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '8px' }}>
              <p style={{ margin: '0 0 6px 0', fontSize: '0.75rem' }}>Calculated fix: <strong>{fc.calculated_value}</strong></p>
              <button 
                style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', width: '100%', fontWeight: '600' }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onApply) onApply(fc.calculated_value);
                  setOpen(false);
                }}
              >
                Apply Fix
              </button>
            </div>
          )}
        </div>
      )}
    </span>
  );
}

// ── GST: derive combined rate/amount/type from per-tax gst_rate ─────────────
function getGstDisplay(bill) {
  const rate = parseFloat(bill.gst_rate) || 0;
  const combinedAmount = bill.is_interstate
    ? parseFloat(bill.igst || 0)
    : (parseFloat(bill.cgst || 0) + parseFloat(bill.sgst || 0));
  // Real tax can exist even when gst_rate reads 0 — that field is
  // Rule-1-protected at whatever the AI literally extracted, which is
  // sometimes "0" even though genuine cgst/sgst amounts were found
  // elsewhere on the bill (e.g. only a cumulative total was printed).
  if (combinedAmount <= 0 && rate === 0) return null;
  const combinedRate = bill.is_interstate ? rate : rate * 2;
  return { combinedRate, combinedAmount, type: bill.is_interstate ? 'IGST' : 'CGST + SGST' };
}

// ── Expandable cell — hover preview via title, multi-line on focus ──────────
function ExpandableCell({ value, onChange, disabled }) {
  const [focused, setFocused] = useState(false);
  if (focused) {
    return (
      <textarea
        className={styles.cellTextarea}
        autoFocus
        value={value}
        disabled={disabled}
        onChange={e => onChange(e.target.value)}
        onBlur={() => setFocused(false)}
        rows={2}
      />
    );
  }
  return (
    <input
      className={styles.cellInput}
      value={value}
      title={value}
      disabled={disabled}
      onFocus={() => setFocused(true)}
      onChange={e => onChange(e.target.value)}
    />
  );
}

// ── Review Dashboard ──────────────────────────────────────────────────────────
function ReviewDashboard({ jobId, onExportDone }) {
  const [activeTab, setActiveTab]     = useState('bills');
  const [bills, setBills]             = useState([]);
  const [transactions, setTxns]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [confirming, setConfirming]   = useState(false);
  const [billsDone, setBillsDone]     = useState(false);
  const [bankDone, setBankDone]       = useState(false);
  const [edits, setEdits]             = useState({});
  const [bankEdits, setBankEdits]     = useState({});
  const [bankLinks, setBankLinks]     = useState({});
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [entryMode, setEntryMode]       = useState('accounting');
  const [aggregated, setAggregated]     = useState(false);
  const [exportError, setExportError] = useState('');
  const [allLedgers, setAllLedgers] = useState([]);
  const [filters, setFilters] = useState({ mismatch: false, confirmation: false });

  useEffect(() => {
    apiSearchLedgers('').then(data => setAllLedgers(data.ledgers || []));
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [b, t] = await Promise.all([apiReviewBills(jobId), apiReviewBank(jobId)]);
      setBills(b.bills || []);
      setTxns(t.transactions || []);
      const links = {};
      (t.transactions || []).forEach(tx => {
        if (tx.suggested_invoice) {
          const suggestedAmt = tx.debit || tx.credit || 0;
          links[tx.bank_result_id] = [{
            result_id:      tx.suggested_invoice,
            invoice_number: tx.suggested_invoice_number || '',
            party_name:     tx.suggested_party || '',
            amount:         suggestedAmt
          }];
        } else {
          links[tx.bank_result_id] = [];
        }
      });
      setBankLinks(links);
      setLoading(false);
    };
    load();
  }, [jobId]);

  const toggleRow = (resultId) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(resultId) ? next.delete(resultId) : next.add(resultId);
      return next;
    });
  };

  const handleFieldEdit = (resultId, field, value) => {
    setEdits(prev => ({
      ...prev,
      [resultId]: { ...(prev[resultId] || {}), [field]: value }
    }));
  };

  const handleBankFieldEdit = (bankResultId, field, value) => {
    setBankEdits(prev => ({
      ...prev,
      [bankResultId]: { ...(prev[bankResultId] || {}), [field]: value }
    }));
  };

  const handleConfirmBills = async () => {
    setConfirming(true);
    try {
      const confirmed    = bills.filter(b => !edits[b.result_id]).map(b => b.result_id);
      const corrections  = edits;
      await apiConfirmBills(jobId, confirmed, corrections);
      const b = await apiReviewBills(jobId);
      setBills(b.bills || []);
      setBillsDone(true);
    } catch { /* ignore */ } finally {
      setConfirming(false);
    }
  };

  const handleConfirmBank = async () => {
    setConfirming(true);
    try {
      await apiConfirmBank(jobId, bankLinks, bankEdits);
      const t = await apiReviewBank(jobId);
      setTxns(t.transactions || []);
      setBankDone(true);
    } catch { /* ignore */ } finally {
      setConfirming(false);
    }
  };

  const handleExport = async (type) => {
    setExportError('');
    try {
      const blob = type === 'bills'
        ? await apiExportBills(jobId, entryMode, aggregated)
        : await apiExportBank(jobId);
      const url = URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href     = url;
      a.download = type === 'bills' ? `Bills_${jobId}.csv` : `Bank_${jobId}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      if (onExportDone) onExportDone(type);
    } catch (err) {
      if (err.detail?.expired) {
        if (onExportDone) onExportDone('expired');
      } else {
        setExportError(err.message || 'Export failed. Please try again.');
      }
    }
  };

  if (loading) return (
    <div className={styles.reviewLoading}>
      <span className={styles.spinnerDark} />
      Loading review data...
    </div>
  );

  const billFlags = (bill) => {
    const fc = parseFieldConfidence(bill.field_confidence);
    const top = ['gstin', 'total_amount', 'party_name', 'invoice_date', 'invoice_number', 'subtotal']
      .map(f => fc[f]?.toggle).filter(Boolean);
    const lines = (fc.line_items || []).flatMap(li => [li.amount?.toggle, li.hsn_code?.toggle]).filter(Boolean);
    return [...top, ...lines];
  };

  const mismatchCount    = bills.filter(b => billFlags(b).includes('mismatch')).length;
  const confirmationCount = bills.filter(b => billFlags(b).includes('confirmation')).length;

  const rowShouldWash = (bill) => {
    const flags = billFlags(bill);
    return (filters.mismatch && flags.includes('mismatch')) ||
          (filters.confirmation && flags.includes('confirmation'));
  };

  return (
    <div className={styles.review}>
      <div className={styles.reviewHeader}>
        <h2 className={styles.reviewTitle}>Review &amp; Confirm</h2>
        <p className={styles.reviewSubtitle}>
          Check extracted data, make corrections, then confirm to download CSVs.
        </p>
        <p className={styles.reviewDisclaimer}>
          <em><strong>Verify your data.</strong> Delefy is an AI assistant, not a replacement for professional review. Please double-check amounts and GSTINs before exporting to Tally.</em>
        </p>
      </div>

      <div className={styles.reviewTabs}>
        <button
          className={`${styles.reviewTab} ${activeTab === 'bills' ? styles.reviewTabActive : ''}`}
          onClick={() => setActiveTab('bills')}
          type="button"
        >
          Bills
          <span className={styles.tabBadge}>{bills.length}</span>
          {billsDone && <span className={styles.tabCheck}>✓</span>}
        </button>
        <button
          className={`${styles.reviewTab} ${activeTab === 'bank' ? styles.reviewTabActive : ''}`}
          onClick={() => setActiveTab('bank')}
          type="button"
        >
          Bank
          <span className={styles.tabBadge}>{transactions.length}</span>
          {bankDone && <span className={styles.tabCheck}>✓</span>}
        </button>
      </div>

      {activeTab === 'bills' && (
        <div className={styles.tabContent}>
          {bills.length === 0 ? (
            <div className={styles.emptyTab}>No bills extracted.</div>
          ) : (
            <>
              <div className={styles.filterToggles}>
                <button
                  type="button"
                  className={`${styles.filterChip} ${filters.mismatch ? styles.filterChipActive : ''}`}
                  onClick={() => setFilters(f => ({ ...f, mismatch: !f.mismatch }))}
                >
                  Mathematical Mismatch <span className={styles.filterCount}>{mismatchCount}</span>
                </button>
                <button
                  type="button"
                  className={`${styles.filterChip} ${filters.confirmation ? styles.filterChipActive : ''}`}
                  onClick={() => setFilters(f => ({ ...f, confirmation: !f.confirmation }))}
                >
                  Needs Confirmation <span className={styles.filterCount}>{confirmationCount}</span>
                </button>
              </div>

              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th style={{width:28}}></th>
                      <th>Date</th>
                      <th>Invoice #</th>
                      <th>Party Name</th>
                      <th>GSTIN</th>
                      <th>Taxable</th>
                      <th>Discount</th>
                      <th>GST Rate</th>
                      <th>GST ₹</th>
                      <th>Round Off</th>
                      <th>Total</th>
                      <th>Ledger</th>
                      <th>New?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bills.map(bill => {
                      const expanded = expandedRows.has(bill.result_id);
                      const lineItems = edits[bill.result_id]?.line_items ?? (bill.line_items || []);
                      const hasItems  = lineItems.length > 0;
                      const be        = edits[bill.result_id] || {};
                      const fc        = parseFieldConfidence(bill.field_confidence);
                      // Merge CA edits before reading GST display — getGstDisplay
                      // otherwise ignores any cgst/sgst/igst fix the user applies,
                      // so the number on screen never visibly changes.
                      const gst       = getGstDisplay({ ...bill, ...be });
                      const wash      = rowShouldWash(bill);

                      // Header becomes a live sum of line items whenever items
                      // exist — no longer independently edited in that case.
                      const roundOffVal = parseFloat(be.round_off ?? bill.round_off ?? 0) || 0;
                      const headerSums = hasItems
                        ? recalcHeaderFromItems(
                            lineItems.map(it => ({
                              gross:    parseFloat(it._gross_taxable ?? it.taxable_amount ?? 0) || 0,
                              discount: parseFloat(it.discount ?? 0) || 0,
                              net:      parseFloat(it.taxable_amount ?? 0) || 0,
                              tax:      (parseFloat(it.cgst ?? 0) || 0) + (parseFloat(it.sgst ?? 0) || 0) + (parseFloat(it.igst ?? 0) || 0),
                            })),
                            roundOffVal
                          )
                        : null;
                      // Only once the CA has actually edited a line item do we
                      // switch the header to live sums — untouched bills always
                      // show their own protected DB values.
                      const itemsEdited = Boolean(edits[bill.result_id]?.line_items);

                      return (
                        <React.Fragment key={bill.result_id}>
                          <tr
                            className={`${bill.is_new_ledger ? styles.rowNew : ''} ${wash ? styles.rowWash : ''}`}
                            onClick={() => hasItems && toggleRow(bill.result_id)}
                            style={hasItems ? {cursor:'pointer'} : {}}
                          >
                            <td>
                              {hasItems && (
                                <span className={`${styles.expandArrow} ${expanded ? styles.expandArrowOpen : ''}`}>›</span>
                              )}
                            </td>
                            <td>
                              <FieldConfWrap fc={fc.invoice_date}>
                                <input className={styles.cellInput} value={be.invoice_date ?? bill.invoice_date ?? ''}
                                  disabled={billsDone}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => handleFieldEdit(bill.result_id, 'invoice_date', e.target.value)} />
                              </FieldConfWrap>
                            </td>
                            <td>
                              <FieldConfWrap fc={fc.invoice_number}>
                                <input className={styles.cellInput} value={be.invoice_number ?? bill.invoice_number ?? ''}
                                  disabled={billsDone}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => handleFieldEdit(bill.result_id, 'invoice_number', e.target.value)} />
                              </FieldConfWrap>
                            </td>
                            <td onClick={e => e.stopPropagation()}>
                              <FieldConfWrap fc={fc.party_name}>
                                <ExpandableCell
                                  value={be.party_name ?? bill.party_name ?? ''}
                                  disabled={billsDone}
                                  onChange={val => handleFieldEdit(bill.result_id, 'party_name', val)}
                                />
                              </FieldConfWrap>
                            </td>
                            <td className={styles.mono}>
                              <FieldConfWrap fc={fc.gstin}>
                                <input className={styles.cellInput} value={be.gstin ?? bill.gstin ?? ''}
                                  disabled={billsDone}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => handleFieldEdit(bill.result_id, 'gstin', e.target.value)} />
                              </FieldConfWrap>
                            </td>
                            <td className={styles.mono}>
                              <FieldConfWrap fc={fc.subtotal}>
                                {itemsEdited ? (
                                  <span className={styles.cellInput} style={{display:'inline-block', background:'#F7F7F5'}}>
                                    {headerSums.grossSum.toFixed(2)}
                                  </span>
                                ) : (
                                  <input className={styles.cellInput} value={be.taxable_amount ?? bill.taxable_amount ?? ''}
                                    disabled={billsDone}
                                    onClick={e => e.stopPropagation()}
                                    onChange={e => handleFieldEdit(bill.result_id, 'taxable_amount', e.target.value)} />
                                )}
                              </FieldConfWrap>
                            </td>
                            <td className={styles.mono}>
                              {hasItems ? headerSums.discSum.toFixed(2) : '—'}
                            </td>
                            <td>
                              {(() => {
                                const editedRate  = parseFloat(be.gst_rate ?? bill.gst_rate ?? 0) || 0;
                                const editedInter = be.is_interstate ?? bill.is_interstate;
                                // gst_rate alone is unreliable evidence of "no tax" — it can sit at
                                // the AI's literal "0" forever (Rule-1 protected) even when real
                                // cgst/sgst/igst amounts prove tax genuinely exists (e.g. only a
                                // cumulative total was printed, no per-line rate for the AI to read).
                                // Same fix already applied to getGstDisplay — mirrored here so the
                                // dropdown and the Rate/₹ columns never disagree with each other.
                                const editedCgst = parseFloat(be.cgst ?? bill.cgst ?? 0) || 0;
                                const editedSgst = parseFloat(be.sgst ?? bill.sgst ?? 0) || 0;
                                const editedIgst = parseFloat(be.igst ?? bill.igst ?? 0) || 0;
                                const hasRealTax = (editedCgst + editedSgst + editedIgst) > 0 || editedRate > 0;
                                const currentTaxType = be.tax_type ?? (!hasRealTax ? 'NIL' : (editedInter ? 'IGST' : 'CGST_SGST'));
                                const handleTaxTypeChange = (val) => {
                                  handleFieldEdit(bill.result_id, 'tax_type', val);
                                  if (val === 'NIL') {
                                    handleFieldEdit(bill.result_id, 'gst_rate', 0);
                                    handleFieldEdit(bill.result_id, 'is_interstate', false);
                                  } else {
                                    handleFieldEdit(bill.result_id, 'is_interstate', val === 'IGST');
                                  }
                                };
                                const taxTypeSelect = (
                                  <select
                                    className={styles.gstTypeBadge}
                                    style={{ cursor: billsDone ? 'default' : 'pointer', border: 'none' }}
                                    value={currentTaxType}
                                    disabled={billsDone}
                                    onClick={e => e.stopPropagation()}
                                    onChange={e => handleTaxTypeChange(e.target.value)}
                                  >
                                    <option value="CGST_SGST">CGST + SGST</option>
                                    <option value="IGST">IGST</option>
                                    <option value="NIL">Nil</option>
                                  </select>
                                );
                                // Rate: sourced from the line items themselves when they exist and
                                // agree — never backward-derived from taxable/total. "Mixed" when
                                // items genuinely carry different rates, rather than picking one
                                // arbitrarily and hiding the disagreement.
                                let rateDisplay = '—';
                                if (itemsEdited || hasItems) {
                                  const rates = lineItems.map(it => {
                                    const r = parseFloat(it.gst_rate ?? 0) || 0;
                                    return bill.is_interstate ? r : round2(r * 2);
                                  });
                                  const uniqueRates = [...new Set(rates)];
                                  rateDisplay = uniqueRates.length === 1 ? `${uniqueRates[0]}%` : 'Mixed';
                                } else if (gst) {
                                  rateDisplay = `${gst.combinedRate}%`;
                                }
                                const valueDisplay = itemsEdited
                                  ? headerSums.taxSum.toFixed(2)
                                  : (gst ? gst.combinedAmount.toFixed(2) : '0.00');
                                return (
                                  <>
                                    <span className={styles.mono}>{rateDisplay}</span>
                                    {taxTypeSelect}
                                  </>
                                );
                              })()}
                            </td>
                            <td className={styles.mono}>
                              <FieldConfWrap
                                fc={fc.gst_total}
                                onApply={(val) => {
                                  const totalTax = parseFloat(val) || 0;
                                  if (bill.is_interstate) {
                                    handleFieldEdit(bill.result_id, 'igst', totalTax);
                                    handleFieldEdit(bill.result_id, 'cgst', 0);
                                    handleFieldEdit(bill.result_id, 'sgst', 0);
                                  } else {
                                    const half = round2(totalTax / 2);
                                    handleFieldEdit(bill.result_id, 'cgst', half);
                                    handleFieldEdit(bill.result_id, 'sgst', half);
                                    handleFieldEdit(bill.result_id, 'igst', 0);
                                  }
                                }}
                              >
                                <span className={styles.cellInput} style={{display:'inline-block', background:'transparent'}}>
                                  {(() => {
                                    const valueDisplay = itemsEdited
                                      ? headerSums.taxSum.toFixed(2)
                                      : (gst ? gst.combinedAmount.toFixed(2) : '0.00');
                                    return `₹${valueDisplay}`;
                                  })()}
                                </span>
                              </FieldConfWrap>
                            </td>
                            <td className={styles.mono}>
                              <input className={styles.cellInput} value={be.round_off ?? bill.round_off ?? 0}
                                disabled={billsDone}
                                onClick={e => e.stopPropagation()}
                                onChange={e => handleFieldEdit(bill.result_id, 'round_off', e.target.value)} />
                            </td>
                            <td className={styles.mono}>
                              <FieldConfWrap 
                                fc={fc.total_amount} 
                                onApply={(val) => handleFieldEdit(bill.result_id, 'total_amount', val)}
                              >
                                {itemsEdited ? (
                                  <span className={styles.cellInput} style={{display:'inline-block', background:'#F7F7F5'}}>
                                    {headerSums.totalSum.toFixed(2)}
                                  </span>
                                ) : (
                                  <input className={styles.cellInput} value={be.total_amount ?? bill.total_amount ?? ''}
                                    disabled={billsDone}
                                    onClick={e => e.stopPropagation()}
                                    onChange={e => handleFieldEdit(bill.result_id, 'total_amount', e.target.value)} />
                                )}
                              </FieldConfWrap>
                            </td>
                            <td>
                              <LedgerSearchInput
                                value={be.tally_ledger ?? bill.tally_ledger ?? ''}
                                disabled={billsDone}
                                ledgers={allLedgers}
                                onChange={(val) => handleFieldEdit(bill.result_id, 'tally_ledger', val)}
                              />
                            </td>
                            <td>
                              {(() => {
                                const current = (be.tally_ledger ?? bill.tally_ledger ?? '').trim().toLowerCase();
                                const isExisting = current && allLedgers.some(l => l.toLowerCase() === current);
                                return isExisting
                                  ? <span className={styles.badgeOk}>EXISTING</span>
                                  : <span className={styles.badgeNew}>NEW</span>;
                              })()}
                            </td>
                          </tr>
                          {expanded && hasItems && (
                            <tr className={styles.expandedRow}>
                              <td colSpan={12}>
                                <div className={styles.lineItems}>
                                  <table className={styles.lineItemsTable}>
                                    <thead>
                                      <tr>
                                        <th>Item Description</th>
                                        <th>HSN Code</th>
                                        <th>Qty</th>
                                        <th>Unit</th>
                                        <th>Rate</th>
                                        <th>Taxable Amt</th>
                                        <th>Discount</th>
                                        <th>GST Rate %</th>
                                        <th>Tax ₹</th>
                                        <th>Total</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {lineItems.map((item, idx) => {
                                        const itemFc = fc.line_items?.[idx] || {};

                                        // Local "state" derived straight from the item's current
                                        // (possibly already-edited) values — no separate state,
                                        // matches the existing edits-as-source-of-truth pattern.
                                        // gst_rate is stored PER-SIDE (platform-wide convention), but
                                        // taxCalculator.js's ratePct is used as the COMBINED rate
                                        // throughout (tax = net × ratePct / 100, no doubling inside that
                                        // file). Convert once, right here, at the boundary — not per-cell.
                                        const storedPerSideRate = parseFloat(item.gst_rate ?? 0) || 0;
                                        const combinedRatePct = bill.is_interstate ? storedPerSideRate : round2(storedPerSideRate * 2);

                                        const state = {
                                          qty:      parseFloat(item.quantity ?? 1) || 1,
                                          rate:     parseFloat(item.rate ?? 0) || 0,
                                          gross:    parseFloat(item._gross_taxable ?? item.taxable_amount ?? 0) || 0,
                                          discount: parseFloat(item.discount ?? 0) || 0,
                                          net:      parseFloat(item.taxable_amount ?? 0) || 0,
                                          ratePct:  combinedRatePct,
                                          tax:      (parseFloat(item.cgst ?? 0) || 0) + (parseFloat(item.sgst ?? 0) || 0) + (parseFloat(item.igst ?? 0) || 0),
                                          total:    parseFloat(item.total ?? 0) || 0,
                                        };

                                        const applyChange = (field, rawValue) => {
                                          const next = recalcLineItem(state, field, rawValue);
                                          const updated = [...lineItems];
                                          const taxSplit = bill.is_interstate
                                            ? { igst: next.tax, cgst: 0, sgst: 0 }
                                            : { igst: 0, cgst: round2(next.tax / 2), sgst: round2(next.tax / 2) };
                                          // next.ratePct is always COMBINED (matches state.ratePct's
                                          // convention) — convert back to per-side before storing in
                                          // gst_rate, so the stored field's meaning never changes.
                                          const combinedOut = field === 'ratePct' ? (parseFloat(rawValue) || 0) : next.ratePct;
                                          const perSideOut = bill.is_interstate ? combinedOut : round2(combinedOut / 2);
                                          updated[idx] = {
                                            ...updated[idx],
                                            quantity:       field === 'qty'      ? rawValue : next.qty,
                                            rate:           field === 'rate'     ? rawValue : next.rate,
                                            _gross_taxable: field === 'gross'    ? rawValue : next.gross,
                                            discount:       field === 'discount' ? rawValue : next.discount,
                                            taxable_amount: field === 'net'      ? rawValue : next.net,
                                            amount:         field === 'net'      ? rawValue : next.net,
                                            gst_rate:       perSideOut,
                                            total:          field === 'total'    ? rawValue : next.total,
                                            _tax_raw:       field === 'tax'      ? rawValue : null,
                                            ...taxSplit,
                                          };
                                          handleFieldEdit(bill.result_id, 'line_items', updated);
                                        };

                                        return (
                                          <tr key={idx}>
                                            <td>
                                              <FieldConfWrap fc={itemFc.description}>
                                                <ExpandableCell
                                                  value={item.description ?? ''}
                                                  disabled={billsDone}
                                                  onChange={val => {
                                                    const updated = [...lineItems];
                                                    updated[idx] = {...updated[idx], description: val};
                                                    handleFieldEdit(bill.result_id, 'line_items', updated);
                                                  }}
                                                />
                                              </FieldConfWrap>
                                            </td>
                                            <td className={styles.mono}>
                                              <FieldConfWrap fc={itemFc.hsn_code}>
                                                <input className={styles.cellInput} style={{width:'80px'}} value={item.hsn_code ?? ''}
                                                  disabled={billsDone}
                                                  onChange={e => {
                                                    const updated = [...lineItems];
                                                    updated[idx] = {...updated[idx], hsn_code: e.target.value};
                                                    handleFieldEdit(bill.result_id, 'line_items', updated);
                                                  }} />
                                              </FieldConfWrap>
                                            </td>
                                            <td className={styles.mono}>
                                              <input className={styles.cellInput} style={{width:'48px'}} value={item.quantity ?? state.qty}
                                                disabled={billsDone}
                                                onChange={e => applyChange('qty', e.target.value)} />
                                            </td>
                                            <td>
                                              <input className={styles.cellInput} style={{width:'48px'}} value={item.unit ?? 'Nos'}
                                                disabled={billsDone}
                                                onChange={e => {
                                                  const updated = [...lineItems];
                                                  updated[idx] = {...updated[idx], unit: e.target.value};
                                                  handleFieldEdit(bill.result_id, 'line_items', updated);
                                                }} />
                                            </td>
                                            <td className={styles.mono}>
                                              <input className={styles.cellInput} style={{width:'72px'}} value={item.rate ?? state.rate}
                                                disabled={billsDone}
                                                onChange={e => applyChange('rate', e.target.value)} />
                                            </td>
                                            <td className={styles.mono}>
                                              <FieldConfWrap fc={itemFc.amount} onApply={(val) => applyChange('gross', val)}>
                                                <input className={styles.cellInput} style={{width:'72px'}} value={item._gross_taxable ?? state.gross}
                                                  disabled={billsDone}
                                                  onChange={e => applyChange('gross', e.target.value)} />
                                              </FieldConfWrap>
                                            </td>
                                            <td className={styles.mono}>
                                              <input className={styles.cellInput} style={{width:'72px'}} value={item.discount ?? state.discount}
                                                disabled={billsDone}
                                                onChange={e => applyChange('discount', e.target.value)} />
                                            </td>
                                            <td className={styles.mono}>
                                              <input className={styles.cellInput} style={{width:'60px'}} value={state.ratePct}
                                                disabled={billsDone}
                                                onChange={e => applyChange('ratePct', e.target.value)} />
                                            </td>
                                            <td className={styles.mono}>
                                              <input className={styles.cellInput} style={{width:'72px'}} value={item._tax_raw ?? state.tax}
                                                disabled={billsDone}
                                                onChange={e => applyChange('tax', e.target.value)} />
                                            </td>
                                            <td className={styles.mono}>
                                              <input className={styles.cellInput} style={{width:'72px'}} value={item.total ?? state.total}
                                                disabled={billsDone}
                                                onChange={e => applyChange('total', e.target.value)} />
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {bills.some(b => b.is_new_ledger) && (
                <div className={styles.newLedgerWarning}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.3"/>
                    <line x1="7" y1="4" x2="7" y2="8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                    <circle cx="7" cy="10" r="0.5" fill="currentColor"/>
                  </svg>
                  New ledgers detected. Create them in Tally before importing the CSV.
                </div>
              )}

              <div className={styles.reviewToggles}>
                <label className={styles.toggleLabel}>
                  <button type="button" className={`${styles.toggleBtn} ${aggregated ? styles.toggleBtnOn : ''}`} onClick={() => setAggregated(p => !p)}>
                    <span className={styles.toggleThumb} />
                  </button>
                  <span>
                    <strong>Aggregate by Tax Rate</strong>
                    <span className={styles.toggleDesc}>{aggregated ? 'Items grouped by GST rate — fewer rows' : 'Full item-wise — one row per item'}</span>
                  </span>
                </label>
                <label className={styles.toggleLabel}>
                  <button type="button" className={`${styles.toggleBtn} ${entryMode === 'inventory' ? styles.toggleBtnOn : ''}`} onClick={() => setEntryMode(p => p === 'accounting' ? 'inventory' : 'accounting')}>
                    <span className={styles.toggleThumb} />
                  </button>
                  <span>
                    <strong>Inventory Entry</strong>
                    <span className={styles.toggleDesc}>{entryMode === 'inventory' ? 'Qty, Unit, Rate included — for stock tracking' : 'Accounting only — no Qty/Unit/Rate'}</span>
                  </span>
                </label>
              </div>

              {exportError && (
                <div className={styles.errorBanner}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.3"/>
                    <line x1="7" y1="4" x2="7" y2="8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                    <circle cx="7" cy="10" r="0.5" fill="currentColor"/>
                  </svg>
                  {exportError}
                </div>
              )}
              <div className={styles.tabActions}>
                {!billsDone ? (
                  <button className={styles.confirmBtn} onClick={handleConfirmBills} disabled={confirming} type="button">
                    {confirming ? <span className={styles.spinner} /> : null}
                    Confirm Bills
                  </button>
                ) : (
                  <button className={styles.exportBtn} onClick={() => handleExport('bills')} type="button">
                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{marginRight:6}}>
                      <path d="M7.5 2v8M4 7l3.5 3.5L11 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 13h11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                    </svg>
                    Download Bills CSV
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'bank' && (
        <div className={styles.tabContent}>
          {transactions.length === 0 ? (
            <div className={styles.emptyTab}>No bank statement uploaded or no transactions extracted.</div>
          ) : (
            <>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Ref No</th>
                      <th>Debit</th>
                      <th>Credit</th>
                      <th>Suggested Match</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(tx => {
                      const bx  = bankEdits[tx.bank_result_id] || {};
                      const bfc = parseFieldConfidence(tx.field_confidence);
                      return (
                      <tr key={tx.bank_result_id}>
                        <td>
                          <input className={styles.cellInput} style={{width:'80px'}} value={bx.transaction_date ?? tx.transaction_date ?? ''}
                            disabled={bankDone}
                            onChange={e => handleBankFieldEdit(tx.bank_result_id, 'transaction_date', e.target.value)} />
                        </td>
                        <td className={styles.descCell}>
                          <FieldConfWrap fc={bfc.description}>
                            <ExpandableCell
                              value={bx.description ?? tx.description ?? ''}
                              disabled={bankDone}
                              onChange={val => handleBankFieldEdit(tx.bank_result_id, 'description', val)}
                            />
                          </FieldConfWrap>
                        </td>
                        <td className={styles.mono}>
                          <FieldConfWrap fc={bfc.instrument_no}>
                            <input className={styles.cellInput} style={{width:'90px'}} value={bx.instrument_no ?? tx.instrument_no ?? ''}
                              disabled={bankDone}
                              onChange={e => handleBankFieldEdit(tx.bank_result_id, 'instrument_no', e.target.value)} />
                          </FieldConfWrap>
                        </td>
                        <td className={styles.mono}>
                          <input className={styles.cellInput} style={{width:'80px'}} type="number" step="0.01" value={bx.debit ?? tx.debit ?? ''} placeholder="—"
                            disabled={bankDone}
                            onChange={e => handleBankFieldEdit(tx.bank_result_id, 'debit', e.target.value)} />
                        </td>
                        <td className={styles.mono}>
                          <input className={styles.cellInput} style={{width:'80px'}} type="number" step="0.01" value={bx.credit ?? tx.credit ?? ''} placeholder="—"
                            disabled={bankDone}
                            onChange={e => handleBankFieldEdit(tx.bank_result_id, 'credit', e.target.value)} />
                        </td>
                        <td>
                          {tx.suggested_invoice_number ? (
                            <span className={styles.suggestionChip}>
                              {tx.suggested_party_name} · {tx.suggested_invoice_number}
                              {tx.match_confidence && (
                                <span className={styles.confidence}>
                                  {Math.round(tx.match_confidence)}%
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className={styles.noMatch}>No match</span>
                          )}
                        </td>
                        <td>
                          <BankMatchSelector
                            txn={{
                              ...tx,
                              debit: bx.debit ?? tx.debit,
                              credit: bx.credit ?? tx.credit,
                            }}
                            bills={bills}
                            selected={bankLinks[tx.bank_result_id] || []}
                            disabled={bankDone}
                            onChange={links => setBankLinks(prev => ({
                              ...prev,
                              [tx.bank_result_id]: links
                            }))}
                          />
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {exportError && (
                <div className={styles.errorBanner}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.3"/>
                    <line x1="7" y1="4" x2="7" y2="8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                    <circle cx="7" cy="10" r="0.5" fill="currentColor"/>
                  </svg>
                  {exportError}
                </div>
              )}
              <div className={styles.tabActions}>
                {!bankDone ? (
                  <button className={styles.confirmBtn} onClick={handleConfirmBank} disabled={confirming} type="button">
                    {confirming ? <span className={styles.spinner} /> : null}
                    Confirm Bank Links
                  </button>
                ) : (
                  <button className={styles.exportBtn} onClick={() => handleExport('bank')} type="button">
                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{marginRight:6}}>
                      <path d="M7.5 2v8M4 7l3.5 3.5L11 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 13h11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                    </svg>
                    Download Bank CSV
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [xmlStatus, setXmlStatus]   = useState(null);
  const [xmlLoading, setXmlLoading] = useState(false);
  const [xmlError, setXmlError]     = useState('');

  const [creditsAvailable, setCreditsAvailable] = useState(null);
  const [limitReached, setLimitReached]         = useState(false);
  const [waitlistJoined, setWaitlistJoined] = useState(
    () => sessionStorage.getItem('ca_waitlist_joined') === 'true'
  );
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  const [bankPasswordRequired, setBankPasswordRequired] = useState(false);
  const [bankPasswordWrong, setBankPasswordWrong]       = useState(false);
  const [bankPassword, setBankPassword]                 = useState('');
  const [bankPasswordHint, setBankPasswordHint]         = useState('');
  const [billFiles, setBillFiles]   = useState([]);
  const [bankFile, setBankFile]     = useState([]);
  const [rejectedInfo, setRejectedInfo]             = useState(null);

  const [phase, setPhase]           = useState('upload');
  const [jobId, setJobId]           = useState(null);
  const [jobStatus, setJobStatus]   = useState(null);
  const [processError, setProcessError] = useState('');
  const [limitMsg, setLimitMsg]     = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const pollRef  = useRef(null);
  const xmlInputRef = useRef(null);

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const triggerFeedbackCheck = useCallback(() => {
    const status = localStorage.getItem('ca_feedback_status');
    if (status === 'completed') return;
    if (status && status.startsWith('snoozed_until_')) {
      const snoozeTime = parseInt(status.split('_')[2], 10);
      if (Date.now() < snoozeTime) return;
    }
    setTimeout(() => setShowFeedbackModal(true), 800);
  }, []);

  const handleCloseFeedback = (action) => {
    if (action === 'snooze') {
      const nextWeek = Date.now() + 7 * 24 * 60 * 60 * 1000;
      localStorage.setItem('ca_feedback_status', `snoozed_until_${nextWeek}`);
    }
    setShowFeedbackModal(false);
  };

  useEffect(() => {
    const load = async () => {
      try {
        await apiDeleteMasterXml().catch(() => {});
        setXmlStatus(null);
        const credits = await apiGetCredits().catch(() => ({ credits_available: 0 }));
        const avail = credits?.credits_available ?? 0;
        setCreditsAvailable(avail);
        if (avail <= 0) setLimitReached(true);
      } catch (e) {
        // ignore
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!jobId || phase !== 'processing') return;
    pollRef.current = setInterval(async () => {
      const status = await apiJobStatus(jobId);
      if (!status) return;

      setJobStatus(status);

      if (status.status === 'review') {
        clearInterval(pollRef.current);
        const processed = status.files_done || 0;
        const discovered = status.bills_count || 0;
        if (processed < discovered && discovered > 0) {
          setRejectedInfo({
            accepted: processed,
            rejected: discovered - processed,
            total: discovered,
            names: []
          });
        }

        // Holding transition for 1500ms to allow final "Ready for Review" tick to lock in
        setTimeout(async () => {
          setPhase('review');
          const credits = await apiGetCredits().catch(() => null);
          if (credits) {
            setCreditsAvailable(credits.credits_available ?? 0);
            if ((credits.credits_available ?? 0) <= 0) setLimitReached(true);
          }
        }, 1500);

      } else if (status.status === 'failed') {
        clearInterval(pollRef.current);
        setProcessError(status.stage_detail || 'Processing failed. Please try again.');
        setPhase('upload');
      }
    }, 1500);
    return () => clearInterval(pollRef.current);
  }, [jobId, phase]);

  const handleXmlUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setXmlLoading(true);
    setXmlError('');
    try {
      await apiUploadMasterXml(file);
      const status = await apiMasterXmlStatus();
      setXmlStatus(status);
    } catch (err) {
      setXmlError(err.message || 'Upload failed');
    } finally {
      setXmlLoading(false);
    }
  };

  const handleXmlRemove = async () => {
    await apiDeleteMasterXml().catch(() => {});
    setXmlStatus(null);
    if (xmlInputRef.current) xmlInputRef.current.value = '';
  };

  const _doProcess = async (info) => {
    setProcessError('');
    setLimitMsg('');
    setRejectedInfo(null);
    setIsSubmitting(true);
    try {
      const data = await apiProcess(billFiles, bankFile[0] || null, bankPassword || null);
      if (data.bills_rejected > 0) {
        setRejectedInfo({
          accepted: data.bills_accepted,
          rejected: data.bills_rejected,
          total:    data.bills_accepted + data.bills_rejected,
          names:    data.rejected_names || []
        });
      }
      setPhase('processing');
      setJobId(data.job_id);
      setJobStatus({ current_stage: 'queued', stage_detail: '', files_done: 0, files_total: data.bills_accepted });
    } catch (err) {
      const detail = err.detail || err.message || '';
      if (typeof detail === 'object' && detail.password_wrong) {
        setBankPasswordWrong(true); setPhase('upload');
      } else if (typeof detail === 'object' && detail.password_required) {
        setBankPasswordRequired(true); setPhase('upload');
      } else if (typeof detail === 'object' && (detail.daily_limit || detail.remaining === 0)) {
        setLimitReached(true); setLimitMsg('No credits remaining.'); setPhase('upload');
      } else if (typeof detail === 'string' && (detail.includes('limit') || detail.includes('429'))) {
        setLimitReached(true); setLimitMsg('No credits remaining.'); setPhase('upload');
      } else {
        setProcessError(typeof detail === 'string' ? detail : 'Processing failed.'); setPhase('upload');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProcess = async () => {
    if (billFiles.length === 0) { setProcessError('Please upload at least one bill file.'); return; }
    if (limitReached) return;
    await _doProcess(null);
  };

  const handleReset = async () => {
    clearInterval(pollRef.current);

    if (jobId) {
      try { await apiCancelJob(jobId); } catch { /* ignore */ }
    }

    setBillFiles([]); setBankFile([]); setPhase('upload'); setJobId(null); setJobStatus(null);
    setProcessError(''); setRejectedInfo(null);
    setBankPasswordRequired(false); setBankPasswordWrong(false); setBankPassword(''); setBankPasswordHint('');
    setXmlStatus(null);
    if (xmlInputRef.current) xmlInputRef.current.value = '';
    try { await apiDeleteMasterXml(); } catch { /* ignore */ }

    const credits = await apiGetCredits().catch(() => null);
    if (credits) {
      setCreditsAvailable(credits.credits_available ?? 0);
      if ((credits.credits_available ?? 0) <= 0) setLimitReached(true);
      else setLimitReached(false);
    }
  };

  const handleBankFileChange = async (filesOrUpdater) => {
    const files = typeof filesOrUpdater === 'function'
      ? filesOrUpdater(bankFile)
      : filesOrUpdater;
    setBankFile(files);
    setBankPasswordRequired(false);
    setBankPasswordWrong(false);
    setBankPassword('');
    setBankPasswordHint('');
    const fname = files.length > 0 ? files[0].name.toLowerCase() : '';
    if (files.length > 0 && (fname.endsWith('.pdf') || fname.endsWith('.xlsx') || fname.endsWith('.xls'))) {
      try {
        const result = await apiCheckBankPassword(files[0]);
        if (result.password_required) {
          setBankPasswordRequired(true);
          setBankPasswordHint(result.password_hint || '');
        }
      } catch { /* ignore */ }
    }
  };

  const handleWaitlist = async () => {
    setWaitlistLoading(true);
    try {
      await apiJoinWaitlist();
      setWaitlistJoined(true);
      sessionStorage.setItem('ca_waitlist_joined', 'true');
    } catch {
      setWaitlistJoined(true);
      sessionStorage.setItem('ca_waitlist_joined', 'true');
    } finally {
      setWaitlistLoading(false);
    }
  };

  const isProcessing = phase === 'processing';

  // Compute purely factual file intake text (live, while discovery is active)
  const intakeText = useMemo(() => {
    if (billFiles.length === 0) return 'Preparing files...';
    const parts = [`${billFiles.length} bill file${billFiles.length > 1 ? 's' : ''}`];
    if (bankFile.length > 0) parts.push('1 bank statement');
    if (xmlStatus?.uploaded) parts.push('1 Master XML');

    if (parts.length === 1) return `Analyzing ${parts[0]}...`;
    if (parts.length === 2) return `Analyzing ${parts[0]} and ${parts[1]}...`;
    return `Analyzing ${parts[0]}, ${parts[1]}, and ${parts[2]}...`;
  }, [billFiles.length, bankFile.length, xmlStatus?.uploaded]);

  // Total files submitted to this job (bills + bank + master), for the
  // Stage 1 "done" message: "Found N bills, 1 bank statement, & 1 Master.xml
  // from X files."
  const totalFilesSubmitted = billFiles.length
    + (bankFile.length > 0 ? 1 : 0)
    + (xmlStatus?.uploaded ? 1 : 0);

  return (
    <div className={styles.page}>
      <header className={styles.topBar}>
        <button className={styles.logoBtn} onClick={handleReset} type="button">
          <span className={styles.logoBtnIcon}>D</span>
          <span className={styles.logoBtnText}>Delefy <span className={styles.logoBtnVersion}>v1.0</span></span>
        </button>
        <div className={styles.topBarRight}>
          <span className={styles.welcomeText}>{user?.full_name || user?.username}</span>
          {PLUS_BUNDLE_LIVE ? (
            <button className={styles.navBtn} onClick={() => navigate('/payment')} type="button">
              Buy Bill Credits
            </button>
          ) : null}
          <button className={styles.navBtn} onClick={() => navigate('/profile')} type="button">
            Profile
          </button>
        </div>
      </header>

      <main className={styles.main}>

        {/* ── Section 1: Ledger Setup ── */}
        {phase === 'upload' && (
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Ledger Setup</h2>
              <p className={styles.sectionDesc}>
                To match your exact ledger names, upload your Master.xml from Tally with your bills &amp; bank statements. This step is optional.
              </p>
            </div>

            <div className={styles.setupRow}>
              <div className={styles.setupCard}>
                <div className={`${styles.indicator} ${xmlStatus?.uploaded ? styles.indicatorGreen : styles.indicatorNeutral}`}>
                  {xmlStatus?.uploaded ? (
                    <>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="7" fill="#1A6B3C"/>
                        <path d="M5 8l2 2 4-4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Master.xml uploaded — {xmlStatus.ledger_count} ledgers loaded</span>
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="7" fill="#AAAAAA"/>
                        <path d="M8 5v3.5M8 10.5v.01" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                      <span>Not uploaded — using default ledger names</span>
                    </>
                  )}
                </div>

                <input ref={xmlInputRef} type="file" accept=".xml" style={{display:'none'}} onChange={handleXmlUpload} />

                <div className={styles.xmlBtnRow}>
                  <button
                    className={styles.xmlBtn}
                    onClick={() => xmlInputRef.current.click()}
                    disabled={xmlLoading}
                    type="button"
                  >
                    {xmlLoading ? <span className={styles.spinnerSm} /> : null}
                    {xmlStatus?.uploaded ? 'Re-upload Master.xml' : 'Upload Master.xml'}
                  </button>

                  {xmlStatus?.uploaded && (
                    <button
                      className={styles.xmlRemoveBtn}
                      onClick={handleXmlRemove}
                      type="button"
                    >
                      Remove
                    </button>
                  )}
                </div>

                {xmlError && <span className={styles.xmlError}>{xmlError}</span>}
              </div>
            </div>
          </section>
        )}

        {/* ── Section 2: Upload Files ── */}
        {phase === 'upload' && (
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Upload Files</h2>
              <p className={styles.sectionDesc}>
                Upload bills and optionally a bank statement. They are processed separately.
              </p>
              {!limitReached && (
                <p className={styles.creditsLine}>
                  Bills credit left: <span className={styles.creditsCount}>{creditsAvailable ?? '...'}</span>
                  {creditsAvailable !== null && creditsAvailable > 0 && (
                    <span className={styles.creditsWarn}> — only your first <span className={styles.creditsCount}>{creditsAvailable}</span> bill(s) will be processed.</span>
                  )}
                </p>
              )}
            </div>

            <div className={styles.uploadGrid}>
              <UploadZone
                label="Upload Bills"
                hint="Click or drag & drop. Max per file limit (6 MB)"
                formats="PDF · JPG · PNG · DOCX · XML"
                accept={BILL_TYPES}
                files={billFiles}
                onFiles={setBillFiles}
                disabled={limitReached}
              />
              <div className={styles.uploadCardWrap}>
                <UploadZone
                  label="Upload Bank Statement"
                  hint="Click or drag & drop. Max per file limit (6 MB)"
                  formats="PDF · XLSX · CSV"
                  accept={BANK_TYPES}
                  files={bankFile}
                  onFiles={handleBankFileChange}
                  disabled={limitReached}
                  single
                />
                {bankPasswordRequired && !limitReached && (
                  <div className={styles.passwordCard}>
                    <p className={styles.passwordLabel}>
                      This {bankFile[0]?.name?.toLowerCase().endsWith('.pdf') ? 'PDF' : 'Excel file'} is password protected.
                      {bankPasswordHint && <span> {bankPasswordHint}</span>}
                    </p>
                    <input type="password" className={styles.passwordInput} placeholder="Enter bank statement password"
                      value={bankPassword} onChange={e => { setBankPassword(e.target.value); setBankPasswordWrong(false); }} />
                    {bankPasswordWrong && <span className={styles.passwordError}>Wrong password. Try again.</span>}
                  </div>
                )}
              </div>
            </div>

            {processError && (
              <div className={styles.errorBanner}>{processError}</div>
            )}

            {rejectedInfo && (
              <div className={styles.rejectedBanner}>
                <div className={styles.rejectedMain}>
                  <span>
                    <strong>{rejectedInfo.accepted} of {rejectedInfo.total} bills</strong> processed.
                    <strong> {rejectedInfo.rejected} bill(s)</strong> were not processed — credits exhausted.
                  </span>
                </div>
                {rejectedInfo.names.length > 0 && (
                  <div className={styles.rejectedNames}>Skipped: {rejectedInfo.names.join(', ')}</div>
                )}
              </div>
            )}

            {limitReached ? (
              <div className={styles.limitReachedCard}>
                <h3 className={styles.limitReachedTitle}>No bill credits remaining.</h3>
                <p className={styles.limitReachedDesc}>You've used all your free credits.</p>
                {PLUS_BUNDLE_LIVE ? (
                  <>
                    <p className={styles.limitReachedDesc}><strong>Delefy Plus Bundle</strong> — Get 200 bill credits at ₹599. No monthly commitment, buy when you need.</p>
                    <div className={styles.limitReachedActions}>
                      <button className={styles.waitlistBtn} onClick={() => navigate('/payment')} type="button">
                        Buy Bill Credits
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className={styles.limitReachedDesc}><strong>Delefy Plus Bundle</strong> is coming soon — 200 bills at ₹599. No monthly commitment, buy when you need.</p>
                    <div className={styles.limitReachedActions}>
                      {!waitlistJoined ? (
                        <button className={styles.waitlistBtn} onClick={handleWaitlist} disabled={waitlistLoading} type="button">
                          {waitlistLoading ? <span className={styles.spinner} /> : 'Request Early Access'}
                        </button>
                      ) : (
                        <button className={styles.waitlistBtnDone} disabled type="button">You're on the list</button>
                      )}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className={styles.actionRow}>
                <button className={styles.generateBtn} onClick={handleProcess} disabled={billFiles.length === 0 || isSubmitting} type="button">
                  {isSubmitting ? <span className={styles.spinnerSm} style={{marginRight:7}} /> : null}
                  {isSubmitting ? 'Submitting...' : 'Generate CSV'}
                  {!isSubmitting && billFiles.length > 0 && <span className={styles.fileCountBadge}>{billFiles.length}</span>}
                </button>
                {(billFiles.length > 0 || bankFile.length > 0) && (
                  <button className={styles.resetBtn} onClick={handleReset} type="button">Clear All</button>
                )}
              </div>
            )}
          </section>
        )}

        {/* ── Section 3: Processing ── */}
        {phase === 'processing' && jobStatus && (
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Processing</h2>
            </div>
            <ProcessingStepper
              jobStatus={jobStatus}
              filesDone={jobStatus.files_done || 0}
              filesTotal={jobStatus.files_total || billFiles.length}
              hasBankFile={bankFile.length > 0}
              intakeText={intakeText}
              discoveredBills={jobStatus.bills_count || 0}
              totalFilesSubmitted={totalFilesSubmitted}
              masterUploaded={!!xmlStatus?.uploaded}
            />
          </section>
        )}

        {/* ── Section 4: Review & Confirm ── */}
        {phase === 'review' && jobId && (
          <section className={styles.section}>
            {rejectedInfo && (
              <div className={styles.rejectedBanner}>
                <div className={styles.rejectedMain}>
                  <span>
                    <strong>{rejectedInfo.accepted} of {rejectedInfo.total} bills</strong> processed — credits exhausted. <strong>{rejectedInfo.rejected} bill(s)</strong> were skipped.
                  </span>
                </div>
                {rejectedInfo.names.length > 0 && (
                  <div className={styles.rejectedNames}>Skipped: {rejectedInfo.names.join(', ')}</div>
                )}
              </div>
            )}
            <ReviewDashboard
              jobId={jobId}
              onExportDone={(type) => {
                if (type === 'expired') {
                  alert('Link expired for security and DPDP compliance. Please process the file again.');
                  handleReset();
                }
              }}
            />
            <div className={styles.startOverRow}>
              <button className={styles.resetBtn} onClick={() => { triggerFeedbackCheck(); handleReset(); }} type="button">
                Start Over
              </button>
            </div>
          </section>
        )}

      </main>

      {showFeedbackModal && (
        <FeedbackWidget onClose={handleCloseFeedback} />
      )}
    </div>
  );
}